import React, { useEffect, useRef, useState } from 'react';
import { Xmark, Send } from 'iconoir-react';
import toast from 'react-hot-toast';
import { eventService } from '../../services/eventService';
import { useAuth } from '../../contexts/AuthContext';

/**
 * Multi-event: shown once right after an already-onboarded user joins a NEW
 * event through an invite link. Their profile (and its objective) carries over
 * automatically; this asks whether that objective also holds for the event they
 * just joined. "Same objective" keeps the carryover untouched. "Something
 * different" runs the per-event intent interview.
 *
 * Why an interview and not a goal picker (Jul-17): the picker offered 5 chips,
 * which map to only 5 of the 13 objectives the matcher understands, and carried
 * no supporting detail. The questions that matter are objective-specific: a
 * person who onboarded to raise funding answered funding_need and timeline; if
 * they now want a co-founder, the slots that decide the match (skills_need,
 * equity_expectations, commitment_level) were never asked, and no chip can
 * supply them. The interview asks that objective's own questions.
 *
 * Fresh signups never see this; onboarding captures their goals.
 */

interface Turn {
  role: 'assistant' | 'user';
  text: string;
}

const EventJoinGoalsPrompt = () => {
  const { pendingEventJoin, clearPendingEventJoin } = useAuth();
  const [interviewing, setInterviewing] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [objective, setObjective] = useState<string | null>(null);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [turns, busy]);

  if (!pendingEventJoin) return null;

  const eventLabel = pendingEventJoin.eventName || 'this event';

  const close = () => {
    setInterviewing(false);
    setSessionId(null);
    setTurns([]);
    setInput('');
    setObjective(null);
    setError('');
    clearPendingEventJoin();
  };

  const beginInterview = async () => {
    setStarting(true);
    setError('');
    try {
      const res = await eventService.startEventIntent(pendingEventJoin.eventId);
      setSessionId(res.session_id);
      setTurns([{ role: 'assistant', text: res.greeting }]);
      setInterviewing(true);
    } catch {
      setError('Could not start. Please try again.');
    } finally {
      setStarting(false);
    }
  };

  // Closing the interview is what makes the intent real: the server writes the
  // per-event vectors and only then records it. A failure here must not look
  // like success, or the user would see an objective the matcher never uses.
  const finish = async (sid: string) => {
    setFinishing(true);
    try {
      const res = await eventService.completeEventIntent(pendingEventJoin.eventId, sid);
      toast.success(
        res.intent_summary
          ? `Saved for ${eventLabel}. Your matches here will update shortly.`
          : `Saved for ${eventLabel}.`,
      );
      close();
    } catch {
      // The session is still alive server-side, so Save can simply be retried.
      setError('Could not save your intent. Please try Save again.');
    } finally {
      setFinishing(false);
    }
  };

  const send = async () => {
    const message = input.trim();
    if (!message || !sessionId || busy) return;
    setInput('');
    setError('');
    setTurns((t) => [...t, { role: 'user', text: message }]);
    setBusy(true);
    try {
      const res = await eventService.eventIntentChat(pendingEventJoin.eventId, sessionId, message);
      setTurns((t) => [...t, { role: 'assistant', text: res.ai_response }]);
      if (res.objective_display) setObjective(res.objective_display);
      if (res.is_complete) await finish(sessionId);
    } catch {
      setError('That did not go through. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className='fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4'>
      <div
        className='bg-white rounded-2xl max-w-md w-full flex flex-col max-h-[85vh]'
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex items-start justify-between p-6 pb-2'>
          <div>
            <h3 className='text-lg font-semibold text-heading-500'>
              {interviewing ? `What you're after at ${eventLabel}` : `You've joined ${eventLabel}`}
            </h3>
            {objective && (
              <p className='text-xs text-primary-600 mt-1'>Here for: {objective}</p>
            )}
          </div>
          <button onClick={close} className='text-gray-400 hover:text-gray-600'>
            <Xmark className='w-5 h-5' />
          </button>
        </div>

        {!interviewing ? (
          <div className='px-6 pb-6 space-y-4'>
            <p className='text-sm text-body-400'>
              Your profile carries over automatically. Are you attending with the
              same objective as usual, or after something different this time?
            </p>
            {error && <p className='text-xs text-red-500'>{error}</p>}
            <div className='flex flex-col gap-2'>
              <button
                onClick={close}
                className='w-full py-3 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors'
              >
                Same objective
              </button>
              <button
                onClick={beginInterview}
                disabled={starting}
                className='w-full py-3 border border-gray-200 rounded-xl font-medium text-heading-500 hover:border-gray-300 transition-colors disabled:opacity-50'
              >
                {starting ? 'Starting...' : 'Something different'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div ref={scrollRef} className='flex-1 overflow-y-auto px-6 py-2 space-y-3'>
              {turns.map((t, i) => (
                <div
                  key={i}
                  className={`flex ${t.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm whitespace-pre-wrap ${
                      t.role === 'user'
                        ? 'bg-primary-500 text-white rounded-br-sm'
                        : 'bg-gray-100 text-body-400 rounded-bl-sm'
                    }`}
                  >
                    {t.text}
                  </div>
                </div>
              ))}
              {(busy || finishing) && (
                <div className='flex justify-start'>
                  <div className='bg-gray-100 px-4 py-2.5 rounded-2xl rounded-bl-sm'>
                    <span className='text-sm text-body-400'>
                      {finishing ? 'Saving...' : 'Typing...'}
                    </span>
                  </div>
                </div>
              )}
            </div>

            <div className='px-6 pt-2 pb-6 space-y-2'>
              {error && <p className='text-xs text-red-500'>{error}</p>}
              <div className='flex gap-2'>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      void send();
                    }
                  }}
                  disabled={busy || finishing}
                  placeholder='Type your answer...'
                  className='flex-1 px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-primary-500 disabled:opacity-50'
                />
                <button
                  onClick={() => void send()}
                  disabled={busy || finishing || !input.trim()}
                  className='px-4 bg-primary-500 text-white rounded-xl disabled:opacity-40'
                >
                  <Send className='w-4 h-4' />
                </button>
              </div>
              {/* Save is always available: the interview finalises on its own when
                  the objective's questions are answered, but a user who has said
                  enough should not be forced to keep going. */}
              <button
                onClick={() => sessionId && void finish(sessionId)}
                disabled={finishing || busy || turns.length < 2}
                className='w-full py-2 text-xs text-body-400 hover:text-heading-500 disabled:opacity-40'
              >
                {finishing ? 'Saving...' : 'Save what I have so far'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EventJoinGoalsPrompt;
